/**
 * Add and edit dialogs for a person (PRD §4.3).
 *
 * Adding always includes the first project assignment, because a person without one has nothing
 * to track against. Editing master data is a Grantonomy action (matrix #5, PLAN.md D-31) — the
 * page never shows this dialog to a company admin, and the API refuses it anyway.
 */

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/ui/date-field";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WorkProfileFields } from "@/components/employees/work-profile-fields";
import {
  isWorkProfileWeekdaysValid,
  resolveWorkWeekdays,
  type WorkProfile,
} from "@/components/employees/work-profile";
import { Skeleton } from "@/components/ui/skeleton";
import { describeError } from "@/lib/errors";
import { toastApiError } from "@/lib/rate-limit";
import {
  useAddEmployee,
  useRdShareLimits,
  useUpdateAssignment,
  useUpdateEmployee,
  useWorkProfileLimits,
} from "@/lib/tenancy";
import { berlinToday, entryDateMaxHorizon } from "@/shared/dates";
import {
  RD_SHARE_LIMITS_DEFAULT,
  defaultRdPercent,
  isRdSharePercentInRange,
  rdShareInputMaxPercent,
  type RdShareLimits,
} from "@/shared/rd-share-limits";
import {
  JOB_DESCRIPTION_MAX,
  percentToShare,
  remainingRdShare,
  shareToPercent,
  type AssignmentSummary,
  type PersonSummary,
  type ProjectSummary,
} from "@/shared/schemas/tenancy";
import {
  WORK_PROFILE_LIMITS_DEFAULT,
  isWorkProfileWithinLimits,
} from "@/shared/work-profile-limits";

const DEFAULT_PROFILE: WorkProfile = {
  weeklyHours: "40",
  workdaysPerWeek: "5",
  workWeekdays: [1, 2, 3, 4, 5],
};

function JobDescriptionField({
  id,
  testId,
  value,
  onChange,
}: {
  id: string;
  testId: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{t("employees.jobDescription")}</Label>
      <Textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={6}
        maxLength={JOB_DESCRIPTION_MAX}
        placeholder={t("employees.jobDescriptionPlaceholder")}
        data-testid={testId}
      />
      <p className="text-xs text-muted-foreground">
        {t("employees.jobDescriptionHint", { count: JOB_DESCRIPTION_MAX - value.length })}
      </p>
    </div>
  );
}

export function AddEmployeeDialog({
  open,
  onOpenChange,
  companyId,
  projects,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  projects: ProjectSummary[];
}) {
  const { t } = useTranslation();
  const rdLimitsQuery = useRdShareLimits();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("employees.addTitle")}</DialogTitle>
          <DialogDescription>{t("employees.addBody")}</DialogDescription>
        </DialogHeader>

        {!rdLimitsQuery.isFetched ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <AddEmployeeForm
            companyId={companyId}
            projects={projects}
            rdLimits={rdLimitsQuery.data ?? RD_SHARE_LIMITS_DEFAULT}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddEmployeeForm({
  companyId,
  projects,
  rdLimits,
  onOpenChange,
}: {
  companyId: string;
  projects: ProjectSummary[];
  rdLimits: RdShareLimits;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const add = useAddEmployee(companyId);
  const limitsQuery = useWorkProfileLimits();
  const limits = limitsQuery.data ?? WORK_PROFILE_LIMITS_DEFAULT;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [position, setPosition] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [role, setRole] = useState<"company_admin" | "employee">("employee");
  const [profile, setProfile] = useState<WorkProfile>(DEFAULT_PROFILE);
  const [projectId, setProjectId] = useState("");
  const [entryDate, setEntryDate] = useState(berlinToday());
  const [leaveDate, setLeaveDate] = useState("");
  const [rdPercent, setRdPercent] = useState(String(defaultRdPercent(null, rdLimits)));
  const [sendInvite, setSendInvite] = useState(true);

  const selectedProject = projects.find((project) => project.id === projectId);
  const rdInputMax = rdShareInputMaxPercent(null, rdLimits);
  const entryHorizon = entryDateMaxHorizon();
  const entryDateMax =
    selectedProject?.endDate && selectedProject.endDate < entryHorizon
      ? selectedProject.endDate
      : entryHorizon;

  function reset() {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPosition("");
    setJobDescription("");
    setRole("employee");
    setProfile(DEFAULT_PROFILE);
    setProjectId("");
    setEntryDate(berlinToday());
    setLeaveDate("");
    setRdPercent(String(defaultRdPercent(null, rdLimits)));
    setSendInvite(true);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const percent = Number(rdPercent);
    if (!isRdSharePercentInRange(percent, rdLimits)) {
      toast.error(
        t("errors.RD_SHARE_OUT_OF_RANGE", {
          min: rdLimits.minPercent,
          max: rdLimits.maxPercent,
        }),
      );
      return;
    }
    const rdShare = percentToShare(percent);
    const maxShare = rdLimits.maxPercent / 100;
    // First assignment is the whole budget — refuse above the configured max before the round-trip.
    if (!(rdShare > 0) || Number.isNaN(rdShare) || rdShare > maxShare + 1e-9) {
      toast.error(
        t("errors.RD_SHARE_EXCEEDS_MAX", {
          remaining: rdLimits.maxPercent,
          max: rdLimits.maxPercent,
        }),
      );
      return;
    }
    if (selectedProject && entryDate < selectedProject.startDate) {
      toast.error(
        t("errors.ASSIGNMENT_ENTRY_BEFORE_PROJECT_START", {
          project_start_date: selectedProject.startDate,
        }),
      );
      return;
    }
    if (selectedProject?.endDate && entryDate > selectedProject.endDate) {
      toast.error(
        t("errors.ASSIGNMENT_ENTRY_AFTER_PROJECT_END", {
          project_end_date: selectedProject.endDate,
        }),
      );
      return;
    }
    if (entryDate > entryHorizon) {
      toast.error(
        t("errors.ASSIGNMENT_ENTRY_TOO_FAR_IN_FUTURE", {
          horizon_date: entryHorizon,
        }),
      );
      return;
    }
    if (leaveDate && leaveDate < entryDate) {
      toast.error(t("errors.ASSIGNMENT_LEAVE_BEFORE_ENTRY", { entry_date: entryDate }));
      return;
    }
    if (leaveDate && selectedProject?.endDate && leaveDate > selectedProject.endDate) {
      toast.error(
        t("errors.ASSIGNMENT_LEAVE_AFTER_PROJECT_END", {
          project_end_date: selectedProject.endDate,
        }),
      );
      return;
    }
    add.mutate(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        position: position.trim() || null,
        jobDescription: jobDescription.trim() || null,
        role,
        weeklyHours: Number(profile.weeklyHours),
        workdaysPerWeek: Number(profile.workdaysPerWeek),
        workWeekdays: resolveWorkWeekdays(profile),
        assignment: {
          projectId,
          entryDate,
          rdShare,
          leaveDate: leaveDate || undefined,
        },
        sendInvite,
      },
      {
        onSuccess: () => {
          toast.success(t("employees.added"));
          reset();
          onOpenChange(false);
        },
        onError: (error) => toastApiError(error),
      },
    );
  }

  const weekdaysReady = isWorkProfileWeekdaysValid(profile);
  const profileWithinLimits = isWorkProfileWithinLimits(
    {
      weeklyHours: Number(profile.weeklyHours),
      workdaysPerWeek: Number(profile.workdaysPerWeek),
    },
    limits,
  );

  return (
    // noValidate: custom toasts for RD% / entry-date (native max would hide remaining %).
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="add-first">{t("employees.firstName")}</Label>
          <Input
            id="add-first"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="add-last">{t("employees.lastName")}</Label>
          <Input
            id="add-last"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="add-email">{t("employees.email")}</Label>
        <Input
          id="add-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <p className="text-xs text-muted-foreground">{t("employees.identifierHint")}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="add-position">{t("employees.position")}</Label>
        <Input
          id="add-position"
          type="text"
          inputMode="text"
          autoComplete="off"
          value={position}
          onChange={(event) => setPosition(event.target.value)}
          maxLength={200}
          placeholder={t("employees.positionPlaceholder")}
          data-testid="add-position"
        />
      </div>

      <JobDescriptionField
        id="add-job-description"
        testId="add-job-description"
        value={jobDescription}
        onChange={setJobDescription}
      />

      <WorkProfileFields
        idPrefix="add"
        value={profile}
        onChange={setProfile}
        maxWeeklyHours={limits.maxWeeklyHours}
        maxWorkdaysPerWeek={limits.maxWorkdaysPerWeek}
        maxHoursPerWorkday={limits.maxHoursPerWorkday}
      />

      <div className="space-y-2">
        <Label htmlFor="add-role">{t("employees.role")}</Label>
        <Select value={role} onValueChange={(value) => setRole(value as typeof role)}>
          <SelectTrigger id="add-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="employee">{t("employees.roleEmployee")}</SelectItem>
            <SelectItem value="company_admin">{t("employees.roleCompanyAdmin")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="add-project">{t("employees.assignProject")}</Label>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger id="add-project">
            <SelectValue placeholder={t("employees.assignProject")} />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {projects.length === 0 && (
          <p className="text-xs text-muted-foreground">{t("employees.noProjectsYet")}</p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="add-entry">{t("employees.assignEntryDate")}</Label>
          <DateField
            id="add-entry"
            value={entryDate}
            min={selectedProject?.startDate}
            max={entryDateMax}
            onChange={setEntryDate}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="add-leave">{t("employees.assignLeaveDate")}</Label>
          <DateField
            id="add-leave"
            value={leaveDate}
            min={entryDate}
            max={selectedProject?.endDate}
            onChange={setLeaveDate}
          />
          <p className="text-xs text-muted-foreground">{t("employees.assignLeaveDateHint")}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="add-rd">
            {t("employees.assignRdShare", {
              min: rdLimits.minPercent,
              max: rdLimits.maxPercent,
            })}
          </Label>
          <Input
            id="add-rd"
            type="number"
            min={rdLimits.minPercent}
            max={rdInputMax}
            step="1"
            value={rdPercent}
            onChange={(event) => setRdPercent(event.target.value)}
            required
          />
          <div className="space-y-0.5" data-testid="add-rd-remaining">
            <p className="text-xs text-muted-foreground">
              {t("employees.assignRdRemaining", {
                max: defaultRdPercent(100, rdLimits),
              })}
            </p>
            <p className="text-[11px] leading-snug text-muted-foreground/80">
              {t("employees.assignRdRemainingHint")}
            </p>
          </div>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={sendInvite}
          onCheckedChange={(checked) => setSendInvite(checked === true)}
        />
        {t("employees.sendInvite")}
      </label>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={add.isPending || !projectId || !weekdaysReady || !profileWithinLimits}
        >
          {t("common.create")}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function EditEmployeeDialog({
  person,
  onOpenChange,
}: {
  person: PersonSummary | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateEmployee();
  const limitsQuery = useWorkProfileLimits();
  const limits = limitsQuery.data ?? WORK_PROFILE_LIMITS_DEFAULT;

  const [firstName, setFirstName] = useState(person?.firstName ?? "");
  const [lastName, setLastName] = useState(person?.lastName ?? "");
  const [email, setEmail] = useState(person?.email ?? "");
  const [position, setPosition] = useState(person?.position ?? "");
  const [jobDescription, setJobDescription] = useState(person?.jobDescription ?? "");
  const [profile, setProfile] = useState<WorkProfile>({
    weeklyHours: person?.weeklyHours != null ? String(person.weeklyHours) : "",
    workdaysPerWeek: person?.workdaysPerWeek != null ? String(person.workdaysPerWeek) : "",
    workWeekdays: person?.workWeekdays ?? [1, 2, 3, 4, 5],
  });

  if (!person) return null;

  const emailChanged = email.trim().toLowerCase() !== person.email;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!person) return;
    update.mutate(
      {
        id: person.id,
        patch: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim().toLowerCase(),
          position: position.trim() || null,
          jobDescription: jobDescription.trim() || null,
          weeklyHours: Number(profile.weeklyHours),
          workdaysPerWeek: Number(profile.workdaysPerWeek),
          workWeekdays: resolveWorkWeekdays(profile),
        },
      },
      {
        onSuccess: () => {
          toast.success(t("employees.updated"));
          onOpenChange(false);
        },
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  const weekdaysReady = isWorkProfileWeekdaysValid(profile);
  const profileWithinLimits = isWorkProfileWithinLimits(
    {
      weeklyHours: Number(profile.weeklyHours),
      workdaysPerWeek: Number(profile.workdaysPerWeek),
    },
    limits,
  );

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("employees.editTitle")}</DialogTitle>
          <DialogDescription>{t("employees.editBody")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="edit-first">{t("employees.firstName")}</Label>
              <Input
                id="edit-first"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-last">{t("employees.lastName")}</Label>
              <Input
                id="edit-last"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-email">{t("employees.email")}</Label>
            <Input
              id="edit-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            {emailChanged && (
              <p className="text-xs text-destructive">{t("employees.emailChangeWarning")}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-position">{t("employees.position")}</Label>
            <Input
              id="edit-position"
              type="text"
              inputMode="text"
              autoComplete="off"
              value={position}
              onChange={(event) => setPosition(event.target.value)}
              maxLength={200}
              placeholder={t("employees.positionPlaceholder")}
              data-testid="edit-position"
            />
          </div>

          <JobDescriptionField
            id="edit-job-description"
            testId="edit-job-description"
            value={jobDescription}
            onChange={setJobDescription}
          />

          <WorkProfileFields
            idPrefix="edit"
            value={profile}
            onChange={setProfile}
            maxWeeklyHours={limits.maxWeeklyHours}
            maxWorkdaysPerWeek={limits.maxWorkdaysPerWeek}
            maxHoursPerWorkday={limits.maxHoursPerWorkday}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={update.isPending || !weekdaysReady || !profileWithinLimits}
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * PDP Live roster combined edit: person master data (Grantonomy) plus this project's
 * assignment entry date / R&D share (Grantonomy) and Project Leave Date (CA + Grantonomy).
 * Company `/employees` keeps the split dialogs.
 */
export function EditEmployeeOnProjectDialog({
  person,
  assignment,
  project,
  onOpenChange,
}: {
  person: PersonSummary;
  assignment: AssignmentSummary | null;
  project: ProjectSummary;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const rdLimitsQuery = useRdShareLimits();
  const canEditAssignment = Boolean(assignment?.canEdit);
  const canEditLeaveDate = Boolean(assignment?.canEditLeaveDate);
  const showAssignment = Boolean(assignment && (canEditAssignment || canEditLeaveDate));

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-lg"
        data-testid="edit-employee-on-project-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t("employees.editTitle")}</DialogTitle>
          <DialogDescription>{t("employees.editBody")}</DialogDescription>
        </DialogHeader>

        {showAssignment && !rdLimitsQuery.isFetched ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <EditEmployeeOnProjectForm
            person={person}
            assignment={assignment}
            project={project}
            canEditAssignment={canEditAssignment}
            canEditLeaveDate={canEditLeaveDate}
            rdLimits={rdLimitsQuery.data ?? RD_SHARE_LIMITS_DEFAULT}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditEmployeeOnProjectForm({
  person,
  assignment,
  project,
  canEditAssignment,
  canEditLeaveDate,
  rdLimits,
  onOpenChange,
}: {
  person: PersonSummary;
  assignment: AssignmentSummary | null;
  project: ProjectSummary;
  canEditAssignment: boolean;
  canEditLeaveDate: boolean;
  rdLimits: RdShareLimits;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const updateEmployee = useUpdateEmployee();
  const updateAssignment = useUpdateAssignment();
  const limitsQuery = useWorkProfileLimits();
  const limits = limitsQuery.data ?? WORK_PROFILE_LIMITS_DEFAULT;

  const remainingShare = remainingRdShare(person.assignments, assignment?.id, rdLimits);
  const remainingPercent = shareToPercent(remainingShare);
  const rdInputMax = rdShareInputMaxPercent(remainingPercent, rdLimits);
  const leaveOnly = Boolean(assignment && !canEditAssignment && canEditLeaveDate);

  const [firstName, setFirstName] = useState(person.firstName);
  const [lastName, setLastName] = useState(person.lastName);
  const [email, setEmail] = useState(person.email);
  const [position, setPosition] = useState(person.position ?? "");
  const [jobDescription, setJobDescription] = useState(person.jobDescription ?? "");
  const [profile, setProfile] = useState<WorkProfile>({
    weeklyHours: person.weeklyHours != null ? String(person.weeklyHours) : "",
    workdaysPerWeek: person.workdaysPerWeek != null ? String(person.workdaysPerWeek) : "",
    workWeekdays: person.workWeekdays ?? [1, 2, 3, 4, 5],
  });
  const [entryDate, setEntryDate] = useState(assignment?.entryDate ?? berlinToday());
  const [leaveDate, setLeaveDate] = useState(assignment?.leaveDate ?? "");
  const [rdPercent, setRdPercent] = useState(
    assignment
      ? String(shareToPercent(assignment.rdShare))
      : String(defaultRdPercent(remainingPercent, rdLimits)),
  );

  const emailChanged = email.trim().toLowerCase() !== person.email;
  const weekdaysReady = isWorkProfileWeekdaysValid(profile);
  const profileWithinLimits = isWorkProfileWithinLimits(
    {
      weeklyHours: Number(profile.weeklyHours),
      workdaysPerWeek: Number(profile.workdaysPerWeek),
    },
    limits,
  );
  const entryHorizon = entryDateMaxHorizon();
  const entryDateMax =
    project.endDate && project.endDate < entryHorizon ? project.endDate : entryHorizon;
  const busy = updateEmployee.isPending || updateAssignment.isPending;

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (canEditAssignment && assignment) {
      const percent = Number(rdPercent);
      if (!isRdSharePercentInRange(percent, rdLimits)) {
        toast.error(
          t("errors.RD_SHARE_OUT_OF_RANGE", {
            min: rdLimits.minPercent,
            max: rdLimits.maxPercent,
          }),
        );
        return;
      }
      const rdShare = percentToShare(percent);
      if (!(rdShare > 0) || Number.isNaN(rdShare)) return;
      if (rdShare > remainingShare + 1e-9) {
        toast.error(
          t("errors.RD_SHARE_EXCEEDS_MAX", {
            remaining: remainingPercent,
            max: rdLimits.maxPercent,
          }),
        );
        return;
      }
      if (entryDate < project.startDate) {
        toast.error(
          t("errors.ASSIGNMENT_ENTRY_BEFORE_PROJECT_START", {
            project_start_date: project.startDate,
          }),
        );
        return;
      }
      if (project.endDate && entryDate > project.endDate) {
        toast.error(
          t("errors.ASSIGNMENT_ENTRY_AFTER_PROJECT_END", {
            project_end_date: project.endDate,
          }),
        );
        return;
      }
      if (entryDate > entryHorizon) {
        toast.error(
          t("errors.ASSIGNMENT_ENTRY_TOO_FAR_IN_FUTURE", {
            horizon_date: entryHorizon,
          }),
        );
        return;
      }
    }

    if ((canEditAssignment || canEditLeaveDate) && assignment) {
      if (leaveDate && leaveDate < entryDate) {
        toast.error(t("errors.ASSIGNMENT_LEAVE_BEFORE_ENTRY", { entry_date: entryDate }));
        return;
      }
      if (leaveDate && project.endDate && leaveDate > project.endDate) {
        toast.error(
          t("errors.ASSIGNMENT_LEAVE_AFTER_PROJECT_END", {
            project_end_date: project.endDate,
          }),
        );
        return;
      }
    }

    try {
      if (!leaveOnly) {
        await updateEmployee.mutateAsync({
          id: person.id,
          patch: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim().toLowerCase(),
            position: position.trim() || null,
            jobDescription: jobDescription.trim() || null,
            weeklyHours: Number(profile.weeklyHours),
            workdaysPerWeek: Number(profile.workdaysPerWeek),
            workWeekdays: resolveWorkWeekdays(profile),
          },
        });
      }

      if (canEditAssignment && assignment) {
        const rdShare = percentToShare(Number(rdPercent));
        await updateAssignment.mutateAsync({
          id: assignment.id,
          patch: { entryDate, rdShare, leaveDate: leaveDate || null },
        });
      } else if (leaveOnly && assignment) {
        await updateAssignment.mutateAsync({
          id: assignment.id,
          patch: { leaveDate: leaveDate || null },
        });
      }

      toast.success(t("employees.updated"));
      onOpenChange(false);
    } catch (error) {
      toast.error(describeError(error));
    }
  }

  return (
    // noValidate: custom toasts for RD% / entry-date (native max would hide remaining %).
    <form
      onSubmit={submit}
      className="space-y-4"
      data-testid="edit-employee-on-project-form"
      noValidate
    >
      <fieldset
        disabled={leaveOnly}
        className="min-w-0 space-y-4 border-0 p-0"
        data-testid="edit-on-project-person-section"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="pdp-edit-first">{t("employees.firstName")}</Label>
            <Input
              id="pdp-edit-first"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pdp-edit-last">{t("employees.lastName")}</Label>
            <Input
              id="pdp-edit-last"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pdp-edit-email">{t("employees.email")}</Label>
          <Input
            id="pdp-edit-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          {emailChanged && (
            <p className="text-xs text-destructive">{t("employees.emailChangeWarning")}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="pdp-edit-position">{t("employees.position")}</Label>
          <Input
            id="pdp-edit-position"
            type="text"
            inputMode="text"
            autoComplete="off"
            value={position}
            onChange={(event) => setPosition(event.target.value)}
            maxLength={200}
            placeholder={t("employees.positionPlaceholder")}
            data-testid="pdp-edit-position"
          />
        </div>

        <JobDescriptionField
          id="pdp-edit-job-description"
          testId="pdp-edit-job-description"
          value={jobDescription}
          onChange={setJobDescription}
        />

        <WorkProfileFields
          idPrefix="pdp-edit"
          value={profile}
          onChange={setProfile}
          maxWeeklyHours={limits.maxWeeklyHours}
          maxWorkdaysPerWeek={limits.maxWorkdaysPerWeek}
          maxHoursPerWorkday={limits.maxHoursPerWorkday}
        />
      </fieldset>

      {assignment && (canEditAssignment || canEditLeaveDate) && (
        <div
          className="space-y-4 border-t border-border pt-4"
          data-testid="edit-on-project-assignment-section"
        >
          <div>
            <h3 className="text-sm font-medium">{t("employees.editAssignment")}</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("employees.editAssignmentBody")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pdp-edit-project">{t("employees.assignProject")}</Label>
            <Input id="pdp-edit-project" value={assignment.projectName} disabled />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="pdp-edit-entry">{t("employees.assignEntryDate")}</Label>
              <DateField
                id="pdp-edit-entry"
                value={entryDate}
                min={project.startDate}
                max={entryDateMax}
                onChange={setEntryDate}
                required
                disabled={!canEditAssignment}
                data-testid="pdp-edit-entry"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pdp-edit-leave">{t("employees.assignLeaveDate")}</Label>
              <DateField
                id="pdp-edit-leave"
                value={leaveDate}
                min={entryDate}
                max={project.endDate}
                onChange={setLeaveDate}
                disabled={!canEditLeaveDate}
                data-testid="pdp-edit-leave"
              />
              <p className="text-xs text-muted-foreground">{t("employees.assignLeaveDateHint")}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pdp-edit-rd">
                {t("employees.assignRdShare", {
                  min: rdLimits.minPercent,
                  max: rdLimits.maxPercent,
                })}
              </Label>
              <Input
                id="pdp-edit-rd"
                type="number"
                min={rdLimits.minPercent}
                max={rdInputMax}
                step="1"
                value={rdPercent}
                onChange={(event) => setRdPercent(event.target.value)}
                required
                disabled={!canEditAssignment}
                data-testid="pdp-edit-rd"
              />
              <div className="space-y-0.5" data-testid="pdp-edit-rd-remaining">
                <p className="text-xs text-muted-foreground">
                  {t("employees.assignRdRemaining", {
                    max: defaultRdPercent(remainingPercent, rdLimits),
                  })}
                </p>
                <p className="text-[11px] leading-snug text-muted-foreground/80">
                  {t("employees.assignRdRemainingHint")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={busy || (!leaveOnly && (!weekdaysReady || !profileWithinLimits))}
        >
          {t("common.save")}
        </Button>
      </DialogFooter>
    </form>
  );
}
